import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Container, Row, Col, Card, CardBody } from 'reactstrap'
import { Link } from 'react-router-dom'
import aes from 'crypto-js/aes'
import sha256 from 'crypto-js/sha256'
import { get, patch } from 'helpers/api_helper'
import { store as socketStore } from 'components/Common/Socket/store'
import { runPlayerCommand } from '../UserAccounts/PlayerAccounts/playerCreationOperation'
import { newPlayerOperationId, readPending, savePending } from '../UserAccounts/PlayerAccounts/playerInvitationState'
import logo from '../../assets/images/logo.svg'

const messages = code => ({
  INVALID_PASSWORD: 'Use 8–30 characters, including at least one special character, and no more than 72 UTF-8 bytes.',
  INVALID_RESET_CODE: 'This link is invalid or no longer available. Request a new invitation or password reset.',
  EXPIRED_RESET_CODE: 'This link has expired. Request a new invitation or password reset.',
  OPERATION_PAYLOAD_CONFLICT: 'This operation already has another password. Check the saved result before continuing.',
}[code] || 'The result could not be confirmed. Check its status before trying again.')

// The code of the link: in the path (`/accounts/:id/set-password/:resetCode`) or, without one, in the fragment
// (`/accounts/:id/set-password#code=…`), which reaches neither the server nor a Referer. A new code is a new link.
export default function SetPassword({ match: { params }, location }) {
  const resetCode = params.resetCode || new URLSearchParams((location?.hash || '').slice(1)).get('code')
  if (!resetCode) return <PasswordCard><p role="alert">{messages('INVALID_RESET_CODE')}</p>
    <Link className="btn btn-primary d-inline-flex align-items-center justify-content-center" to="/login">Sign in to Control Panel</Link></PasswordCard>
  return <PasswordForm key={params.id + ':' + resetCode} id={params.id} resetCode={resetCode} />
}
const PasswordCard = ({ children }) => <div className="account-pages my-5 pt-sm-5"><Container><Row className="justify-content-center"><Col md={8} lg={6} xl={5}>
  <Card><CardBody className="p-4">
    <img src={logo} alt="Sportya" height="34" className="mb-4" />
    <h4>Set password</h4>
    {children}
  </CardBody></Card>
</Col></Row></Container></div>
function PasswordForm({ id, resetCode }) {
  const { socket, socketClientId } = useContext(socketStore)?.state?.socket || {}
  const storageKey = 'sportya-password:' + id + ':' + sha256(resetCode).toString()
  const pending = useRef(readPending(storageKey)), request = useRef(null), busy = useRef(false)
  const [link, setLink] = useState(null), [loadError, setLoadError] = useState(''), [reload, setReload] = useState(0)
  const [status, setStatus] = useState(pending.current ? 'unknown' : 'idle'), [result, setResult] = useState(null)
  const [password, setPassword] = useState(''), [confirmation, setConfirmation] = useState('')
  const [errors, setErrors] = useState({}), [message, setMessage] = useState('')
  const locked = status === 'pending' || status === 'unknown'
  useEffect(() => {
    let active = true
    setLoadError(''); setLink(null)
    get('/accounts/' + id + '/password-link', { headers: { 'X-Password-Code': resetCode } }).then(response => {
      if (!['valid', 'used'].includes(response.data?.state)) throw new Error('Invalid response')
      if (active) setLink(response.data)
    }).catch(error => {
      if (active) setLoadError([400, 410].includes(error.response?.status) ? messages(error.response.data?.message) : 'The link could not be checked. Retry when the connection is available.')
    })
    return () => { active = false }
  }, [id, resetCode, reload])
  const track = useCallback(async (attempt, value) => {
    if (busy.current) return
    busy.current = true; pending.current = attempt; savePending(storageKey, attempt)
    setStatus('pending'); setMessage('')
    const operation = runPlayerCommand({ ...attempt, operation: 'password-set', socket,
      isSuccess: data => data?.status === 'completed', messageForCode: messages,
      read: async operationId => {
        try {
          const response = await get('/accounts/' + id + '/password-operations/' + operationId, { headers: { 'X-Password-Code': resetCode } })
          return { data: response.data?.status === 'error' ? { ...response.data, message: response.data.code } : response.data }
        } catch (error) {
          const code = error.response?.data?.message
          if ([400, 410].includes(error.response?.status) && ['INVALID_RESET_CODE', 'EXPIRED_RESET_CODE'].includes(code)) return { data: { status: 'error', message: code } }
          throw error
        }
      },
      submit: value ? () => patch('/accounts/' + id + '/set-password', { resetCode, operationId: attempt.operationId, socketClientId,
        password: aes.encrypt(value, process.env.REACT_APP_ENC_KEY).toString() }, { headers: { 'X-Correlation-Id': attempt.correlationId } }) : undefined,
    })
    request.current = operation
    const outcome = await operation.promise
    if (request.current !== operation) return
    request.current = null; busy.current = false
    if (outcome.status === 'cancelled') return
    setStatus(outcome.status)
    if (['completed', 'error'].includes(outcome.status)) { pending.current = null; savePending(storageKey, null) }
    if (outcome.status === 'completed') { setPassword(''); setConfirmation(''); setResult(outcome) }
    if (outcome.status === 'error') {
      if (['INVALID_RESET_CODE', 'EXPIRED_RESET_CODE'].includes(outcome.code)) { setLink(null); setLoadError(outcome.message) }
      else setMessage(outcome.message)
    }
  }, [id, resetCode, socket, socketClientId, storageKey])
  useEffect(() => {
    const timer = setTimeout(() => { if (pending.current) track(pending.current) }, 0)
    return () => { clearTimeout(timer); const operation = request.current; request.current = null; busy.current = false; operation?.cancel() }
  }, [track])
  const submit = event => {
    event.preventDefault()
    if (busy.current || status === 'pending' || link?.state !== 'valid') return
    const invalid = {}
    if (password.length < 8 || password.length > 30 || unescape(encodeURIComponent(password)).length > 72 || !/[^A-Za-z 0-9]/.test(password)) invalid.password = messages('INVALID_PASSWORD')
    if (confirmation !== password) invalid.confirmation = 'The passwords must match.'
    setErrors(invalid)
    if (Object.keys(invalid).length) { document.getElementById('set-' + Object.keys(invalid)[0])?.focus(); return }
    if (!process.env.REACT_APP_ENC_KEY) { setMessage('Password setup is temporarily unavailable. Please try again later.'); return }
    track({ operationId: pending.current?.operationId || newPlayerOperationId(), correlationId: newPlayerOperationId() }, password)
  }
  return <PasswordCard>
      {result ? <div role="status"><p>{result.activated && link?.purpose === 'admin-invitation' ? 'Password saved. Your administrative account is now active.' : 'Your password has been saved.'}</p>
        <Link className="btn btn-primary d-inline-flex align-items-center justify-content-center" to="/login">Sign in to Control Panel</Link></div> : <>
        {!link && !loadError && <p role="status">Checking your link…</p>}
        {loadError && <div role="alert"><p>{loadError}</p><button type="button" className="btn btn-light" onClick={() => setReload(value => value + 1)}>Retry link check</button></div>}
        {link?.state === 'used' && !locked && <p role="status">This link has already been used. <Link to="/login">Sign in to Control Panel</Link></p>}
        {message && <p role="alert" className="text-danger">{message}</p>}
        {link?.state === 'valid' && <form aria-label="Set password" onSubmit={submit} noValidate>
          <p>{link.purpose === 'admin-invitation' ? 'Choose a password to activate your administrative account.' : 'Choose your new password.'}</p>
          <p id="password-rules" className="text-muted">Use 8–30 characters, including at least one special character. Maximum 72 UTF-8 bytes.</p>
          {['password', 'confirmation'].map(name => <div className="form-group" key={name}>
            <label htmlFor={'set-' + name}>{name === 'password' ? 'Password' : 'Confirm password'}</label>
            <input id={'set-' + name} className="form-control" type="password" autoComplete="new-password" aria-required="true" aria-invalid={Boolean(errors[name])}
              aria-describedby={'password-rules set-' + name + '-error'} value={name === 'password' ? password : confirmation} disabled={status === 'pending'}
              onChange={event => (name === 'password' ? setPassword : setConfirmation)(event.target.value)} />
            <small id={'set-' + name + '-error'} className="text-danger">{errors[name]}</small>
          </div>)}
          <button type="submit" className="btn btn-primary d-inline-flex align-items-center justify-content-center" disabled={status === 'pending'}>{status === 'unknown' ? 'Resume password setup' : 'Set password'}</button>
        </form>}
        {status === 'pending' && <p role="status" className="mt-3">Waiting for password confirmation…</p>}
        {status === 'unknown' && <div className="alert alert-warning mt-3" role="status"><p>The result is not confirmed. Check its status, or re-enter the same password to resume this operation.</p>
          <button type="button" className="btn btn-light" onClick={() => track(pending.current)}>Check password status</button></div>}
      </>}
  </PasswordCard>
}
